import { useEffect, useState } from 'react';
import { api } from '../../../services/api';
import { getUser } from '../../../services/auth';
import { atfStatusInfo } from './acompanhar/atfStatusInfo';
import { lerEmissaoEmCurso } from './emitir/retomada';
import { dataDoDia } from '../formato';
import { SaldoCardHero } from '../SaldoCardHero';
import { useSaldoResumo } from '../useSaldoResumo';
import { Selo } from '../../common/interface';
import type { HistoricoAtfResponse } from './acompanhar/types';
import type { CooperadoAba } from '../types';

interface Props {
  onNavegar: (aba: CooperadoAba) => void;
}

/**
 * A cidade legível, com o índice do DER como último recurso — a home tinha a
 * mesma concatenação crua do card, e mostrava "684 → 65".
 */
function cidade(nome: string | undefined, uf: string | undefined, indice: number): string {
  if (!nome) return String(indice);
  return uf ? `${nome}/${uf}` : nome;
}

function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? nome;
}

function saudacao(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
}

/** Início (handoff §1): emissão em curso, atalhos, últimas ATFs, card de saldo. */
export function InicioAba({ onNavegar }: Props) {
  const usuario = getUser();
  const { saldo, tipoEmissao } = useSaldoResumo();
  const [atfs, setAtfs] = useState<HistoricoAtfResponse | null>(null);
  // A contagem dizia "0 ATF em andamento" quando a leitura falhava: zero no
  // lugar do que não foi lido (a regra da 0088).
  const [erroAtfs, setErroAtfs] = useState(false);
  const emissaoEmCurso = usuario?.idUsuario != null ? lerEmissaoEmCurso(usuario.idUsuario, Date.now()) : null;

  useEffect(() => {
    api.get<HistoricoAtfResponse>('/cooperado/atf', { params: { limit: 3, offset: 0 } })
      .then(res => { setAtfs(res.data); setErroAtfs(false); })
      .catch(() => { setAtfs(null); setErroAtfs(true); });
  }, []);

  const emAndamento = atfs?.data.filter(i => atfStatusInfo(i.status, i.chPlc).emAndamento).length ?? 0;

  return (
    <div>
      <h1 className="co-titulo-tela">
        {saudacao()}, {primeiroNome(usuario?.nomeUsuario ?? usuario?.usuario ?? 'cooperado')}
      </h1>
      <p className="co-apoio">Você emite, acompanha e paga por aqui.</p>

      {/* Sem saldo (pós-pago), o cartão não existe e a segunda coluna ficava vazia. */}
      <div className={`co-inicio${saldo ? '' : ' co-inicio-sem-saldo'}`}>
        <div className="co-inicio-coluna">
          {emissaoEmCurso && (
            <section className="co-faixa" aria-labelledby="co-faixa-titulo">
              <div className="co-faixa-texto">
                <span className="co-pulso" aria-hidden="true" />
                <div>
                  <h2 id="co-faixa-titulo" className="co-titulo-cartao">Uma emissão ficou parada</h2>
                  <p className="co-apoio">
                    Documento nº {emissaoEmCurso.numCte}
                    {emissaoEmCurso.resumo ? `, ${emissaoEmCurso.resumo.origem} → ${emissaoEmCurso.resumo.destino}` : ''}
                    {' '}· falta pedir a ATF
                  </p>
                </div>
              </div>
              <button type="button" className="co-btn co-btn-primario" onClick={() => onNavegar('emitir')}>
                Continuar emissão
              </button>
            </section>
          )}

          <section className="co-cartao" aria-labelledby="co-fretamento">
            <h2 id="co-fretamento" className="co-rotulo">Fretamento</h2>
            <p className="co-apoio">
              {erroAtfs ? 'Não foi possível ler as suas ATFs agora.' : `${emAndamento} ATF em andamento`}
            </p>
            <div className="co-atalhos">
              <AtalhoCard titulo="Emitir ATF" apoio="Sete passos curtos" onClick={() => onNavegar('emitir')} />
              <AtalhoCard
                titulo="Minhas ATFs"
                apoio={atfs ? `${atfs.total} ao todo` : 'Acompanhar'}
                onClick={() => onNavegar('acompanhar')}
              />
              {/* O atalho segue o regime: para os 195 pós-pago, "créditos" não
                  é o que eles precisam ver. */}
              {tipoEmissao === 'PREPAGO' ? (
                <AtalhoCard titulo="Meus créditos" apoio="Ver saldo e recarregar" onClick={() => onNavegar('creditos')} />
              ) : (
                <AtalhoCard titulo="Minhas faturas" apoio="Ver o que está em aberto" onClick={() => onNavegar('faturas')} />
              )}
            </div>
          </section>

          {atfs && atfs.data.length > 0 && (
            <section className="co-cartao" aria-labelledby="co-ultimas">
              <div className="co-cabecalho">
                <h2 id="co-ultimas" className="co-rotulo">Últimas ATF</h2>
                <button type="button" className="co-btn co-btn-texto" onClick={() => onNavegar('acompanhar')}>
                  Ver todas
                </button>
              </div>
              <ul className="co-lista-curta">
                {atfs.data.map(item => {
                  const info = atfStatusInfo(item.status, item.chPlc);
                  return (
                    <li key={item.id}>
                      <div className="co-lista-curta-texto">
                        <div className="co-lista-curta-titulo">{item.numeroAtf ? `ATF ${item.numeroAtf}` : 'Aguardando o DER'}</div>
                        <div className="co-lista-curta-apoio">
                          {cidade(item.municipioOrigemNome, item.municipioOrigemUf, item.municipioOrigem)}
                          {' → '}
                          {cidade(item.municipioDestinoNome, item.municipioDestinoUf, item.municipioDestino)}
                          {' · '}{item.placa} · {dataDoDia(item.dataIda)}
                        </div>
                      </div>
                      <Selo papel={info.papel}>{info.rotulo}</Selo>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>

        <SaldoCardHero onComprarCredito={() => onNavegar('creditos')} />
      </div>
    </div>
  );
}

function AtalhoCard({ titulo, apoio, onClick }: { titulo: string; apoio: string; onClick: () => void }) {
  return (
    <button type="button" className="co-atalho" onClick={onClick}>
      <span className="co-atalho-titulo">{titulo}</span>
      <span className="co-atalho-apoio">{apoio}</span>
    </button>
  );
}
